import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { Trans, useTranslation } from "react-i18next";
import whitelabel from "../../whitelabel";
import {
	AssetCategory,
	GetAllAssetsParams,
	MODIFY_SPECIFIC_ASSET_UNPAIR_DEVICE_ID,
	VelavuAPI,
	VelavuAsset,
} from "velavu-js-api";
import AssetCategorization, {
	convertCategoryGroupsToAssetCategorizations,
} from "../../data/asset-categorization";
import IconAdd from "../../dynamicicons/icon-add";
import IconVelavuAssets from "../../dynamicicons/icon-velavu-assets";
import AssetFilterMenu from "../../elements/asset-filter-menu";
import { CategoryTag, GroupTag } from "../../elements/categorization-tag";
import Checkbox from "../../elements/checkbox";
import Divider from "../../elements/divider";
import ListHeader from "../../elements/list-header";
import ListPagination from "../../elements/list-pagination";
import LoadingDots from "../../elements/loading-dots";
import StatusIndicator from "../../elements/status-indicator";
import ToggleableFade from "../../elements/toggleable-fade";
import VelavuButton from "../../elements/velavu-button";
import { WeightedTable } from "../../elements/weighted-table";
import { useVelavuModal } from "../../elements/velavu-modal";
import { VelavuTitledModal } from "../../elements/velavu-titled-modal";
import VelavuAlertModalTextConfirm from "../../elements/velavu-alert-modal/velavu-alert-modal-text-confirm";
import { useToggleable } from "../../helper/hook-helper";
import { makeStyleable } from "../../helper/icon-helper";
import { deepCompare } from "../../helper/object-helper";
import { getAssetStatus } from "../../helper/status-helper";
import { classArr } from "../../helper/style-helper";
import { NoAssetsIcon } from "../../img/icons/icons";
import ScreenContainer from "../../screens/screen-container";
import { useListAssetsState } from "../../state/list-assets-state";
import * as styles from "./assets.module.scss";
import * as tableStyles from "../../styles/table.module.scss";
import ConnectionStatus from "../../data/connection-status";
import {
	useHasPermission,
	useSignInContext,
} from "../sign-in-context/sign-in-context";
import { useProvideContentRefreshable } from "../../elements/refresh-context";
import DetailModalLoadProxy from "../asset-tag-detail/detail-modal-load-proxy";
import { ScreenContentContainer } from "../../screens/screen-content-container";
import { getPermissionDeniedMessage } from "../../helper/permission-helper";
import { useGlobalEmitter } from "../../emitter/emitter-context";

function Loading() {
	return (
		<div className={styles.loading}>
			<LoadingDots size={40} color={whitelabel.palette.secondary500} />
		</div>
	);
}

function NoAssets() {
	const { t } = useTranslation();

	return (
		<div className={styles.emptyContent}>
			<NoAssetsIcon />
			<span className={styles.heading}>
				{t("assets.no_assets_heading")}
			</span>
			<span className={styles.info}>{t("assets.no_assets_info")}</span>
		</div>
	);
}

interface AssetActionInProgessModalProps {
	title: string;
	action: string;
	progressCurrent: number;
	progressTotal: number;
}

function AssetActionInProgessModal(props: AssetActionInProgessModalProps) {
	const { title, action, progressCurrent, progressTotal } = props;
	const { t } = useTranslation();
	const emitter = useGlobalEmitter();

	const actionText = useMemo(() => {
		switch (action) {
			case "delete":
				return t("assets.progress_deleted");
			default:
				return t("assets.progress_unpaired");
		}
	}, [action, t]);

	return (
		<ToggleableFade
			visible={progressCurrent !== progressTotal}
			shift
			className={styles.progressScrim}
		>
			<div className={styles.progressContainer}>
				<div className={styles.progressHeader}>{title}</div>
				<Divider />
				<div className={styles.progressBody}>
					{`${progressCurrent}/${progressTotal} ${actionText}`}
				</div>
			</div>
		</ToggleableFade>
	);
}

export default function Assets() {
	const { t } = useTranslation();
	const emitter = useGlobalEmitter();

	useEffect(() => {
		document.title = `${whitelabel.name} | ${t("assets.page_title")}`;
	}, [t]);

	return (
		<ScreenContainer>
			<AssetsList />
		</ScreenContainer>
	);
}

function AssetName(props: { asset: VelavuAsset; onClick?: VoidFunction }) {
	return (
		<div className={styles.assetName}>
			<StatusIndicator status={getAssetStatus(props.asset)} />
			<div className={styles.assetLabel} onClick={props.onClick}>
				{props.asset.name}
			</div>
		</div>
	);
}

function AssetDevice(props: {
	asset: VelavuAsset;
	onPair: VoidFunction;
	canPair?: boolean;
}) {
	const { t } = useTranslation();
	const { canPair, onPair } = props;

	const handlePair = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			event.stopPropagation();
			onPair();
		},
		[onPair],
	);

	if (props.asset.device_id !== undefined) {
		return (
			<div className={styles.assetDevice}>{props.asset.device_id}</div>
		);
	}

	return (
		<VelavuButton
			label={t("assets.pair_tag")}
			icon={(size, color, className) => (
				<IconAdd size={size} color={color} className={className} />
			)}
			size="small"
			disabled={!canPair}
			tooltip={
				!canPair
					? getPermissionDeniedMessage("assets:write", t)
					: undefined
			}
			onClick={handlePair}
		/>
	);
}

function AssetsList() {
	const { t } = useTranslation();
	const emitter = useGlobalEmitter();

	const canWriteAssets = useHasPermission("assets:write");
	const canDeleteAssets = useHasPermission("assets:delete");

	const [searchText, setSearchText] = useState("");

	const scrollableBodyRef = useRef<HTMLDivElement | null>(null);
	const [scrollToTop, setScrollToTop] = useState(false);
	useEffect(() => {
		if (scrollToTop) {
			scrollableBodyRef.current?.scrollTo(0, 0);
			setScrollToTop(false);
		}
	}, [scrollToTop, setScrollToTop]);

	const [actionProgress, setActionProgress] = useState(0);
	const [actionInProgress, setActionInProgress] = useState<
		"delete" | "unpair" | undefined
	>(undefined);

	const [totalPages, setTotalPages] = useState<number>(1);
	const [currentPage, setCurrentPage] = useState<number>(1);

	const [assetsState, assetsActions] = useListAssetsState();
	const [selectedAssets, setSelectedAssets] = useState<string[]>([]);

	const [filterStatus, setFilterStatus] = useState<ConnectionStatus | null>(
		null,
	);
	const [filterUnpaired, setFilterUnpaired] = useState<boolean>(false);
	const [filterCategory, setFilterCategory] = useState<AssetCategory | null>(
		null,
	);
	const [filterCategorization, setFilterCategorization] =
		useState<AssetCategorization | null>(null);
	const [appliedAssetFilters, setAppliedAssetFilters] =
		useState<GetAllAssetsParams>({});

	const assetFilters = useMemo<GetAllAssetsParams>(() => {
		const params: GetAllAssetsParams = {};

		if (filterUnpaired) {
			params.paired = false;
		}

		if (filterCategorization !== null) {
			params.category = filterCategorization.category;
			params.group = filterCategorization.group;
		} else if (filterCategory !== null) {
			params.category = filterCategory;
		}

		switch (filterStatus) {
			case "active":
				params.online = true;
				break;
			case "inactive":
				params.online = false;
				break;
			case "registered":
				params.only_registered = true;
				break;
		}

		return params;
	}, [filterCategorization, filterCategory, filterUnpaired, filterStatus]);

	const isDirty = useMemo(() => {
		return !deepCompare(appliedAssetFilters, assetFilters);
	}, [appliedAssetFilters, assetFilters]);

	const assetsActionsLoadAssets = assetsActions.loadAssets;
	const refetchAssets = useCallback(
		(params: GetAllAssetsParams) => {
			setTotalPages(1);
			setCurrentPage(1);
			return assetsActionsLoadAssets(params);
		},
		[setTotalPages, setCurrentPage, assetsActionsLoadAssets],
	);

	const activeGetAssetsParams = useMemo<GetAllAssetsParams>(() => {
		let cleanSearchText: string | undefined = searchText.trim();
		if (cleanSearchText.length === 0) {
			cleanSearchText = undefined;
		}
		return {
			...appliedAssetFilters,
			name: cleanSearchText,
		};
	}, [searchText, appliedAssetFilters]);

	const refetchEffectFirstMount = useRef(true);
	const refetchEffectLastAppliedAssetFilters = useRef(appliedAssetFilters);
	useEffect(() => {
		//Don't run on first mount to avoid double loading
		if (refetchEffectFirstMount.current) {
			refetchEffectFirstMount.current = false;
			return;
		}

		//If the asset filters have changed, apply the update immediately
		if (
			!deepCompare(
				refetchEffectLastAppliedAssetFilters.current,
				appliedAssetFilters,
			)
		) {
			refetchEffectLastAppliedAssetFilters.current = appliedAssetFilters;
			refetchAssets(activeGetAssetsParams);
		} else {
			//Debounce the refetch to avoid spamming the server
			//as the user is typing
			const timeoutID = window.setTimeout(() => {
				refetchAssets(activeGetAssetsParams);
			}, 300);
			return () => {
				window.clearTimeout(timeoutID);
			};
		}
	}, [refetchAssets, appliedAssetFilters, activeGetAssetsParams]);

	//Register the refreshable provider
	useProvideContentRefreshable(
		useCallback(
			() => refetchAssets(activeGetAssetsParams),
			[refetchAssets, activeGetAssetsParams],
		),
	);

	const applyFilters = useCallback(() => {
		setAppliedAssetFilters(assetFilters);
	}, [setAppliedAssetFilters, assetFilters]);

	const displayAssets = useMemo(() => {
		// Render assets based on page number (limit of 250 per page)
		const startIndex = (currentPage - 1) * 250;
		const endIndex = currentPage * 250;

		return assetsState.assets.slice(startIndex, endIndex);
	}, [assetsState.assets, currentPage]);

	const loadMoreAssets = useCallback(() => {
		if (assetsState.hasMore) {
			assetsActions.loadMore();
			setTotalPages((totalPages) => totalPages + 1);
		}

		setCurrentPage((currentPage) => currentPage + 1);
	}, [assetsState.hasMore, assetsActions]);

	const userContext = useSignInContext();
	const categoryGroups =
		userContext.organization?.meta?.assets?.category_groups;
	const assetCategorizations = useMemo<AssetCategorization[]>(() => {
		if (categoryGroups === undefined) {
			return [];
		} else {
			return convertCategoryGroupsToAssetCategorizations(categoryGroups);
		}
	}, [categoryGroups]);

	const { pushModal } = useVelavuModal();
	const inspectAsset = useCallback(
		(asset: VelavuAsset, defaultOpenPair?: undefined | "asset" | "tag") => {
			pushModal(
				(close) => (
					<VelavuTitledModal
						title={t("assets.asset_details")}
						onClose={close}
					>
						<DetailModalLoadProxy
							asset={asset}
							deviceID={asset.device_id}
							defaultTab="asset"
							defaultOpenPair={defaultOpenPair}
							onClose={close}
						/>
					</VelavuTitledModal>
				),
				{ pinToTop: true },
			);
		},
		[pushModal, assetsActions, t],
	);

	const deleteSelectedAssets = useCallback(() => {
		const confirmDeleteText = t("assets.confirm_delete");
		pushModal<boolean>((resolve) => (
			<VelavuAlertModalTextConfirm
				title={t("assets.delete_modal_title", {
					count: selectedAssets.length,
				})}
				confirmText={confirmDeleteText}
				confirmLabel={t("common.delete")}
				isDanger
				onResolve={resolve}
			>
				<Trans
					i18nKey="assets.delete_confirm_instruction"
					values={{ confirmText: confirmDeleteText }}
					components={{ i: <i /> }}
					parent="span"
				/>
			</VelavuAlertModalTextConfirm>
		)).then(async (result) => {
			if (result) {
				setActionInProgress("delete");

				for (let i = 0; i < selectedAssets.length; i++) {
					try {
						assetsActions.deleteAsset(selectedAssets[i]);
						setActionProgress(i + 1);
					} catch (error) {
						console.error(error);
					}
				}

				setSelectedAssets([]);
				setActionInProgress(undefined);
				setActionProgress(0);
			}
		});

		return () => {
			setActionInProgress(undefined);
		};
	}, [pushModal, selectedAssets, assetsActions, t]);

	const unpairSelectedAssets = useCallback(() => {
		const confirmUnpairText = t("assets.confirm_unpair");
		pushModal<boolean>((resolve) => (
			<VelavuAlertModalTextConfirm
				title={t("assets.unpair_modal_title", {
					count: selectedAssets.length,
				})}
				confirmText={confirmUnpairText}
				confirmLabel={t("common.unpair")}
				onResolve={resolve}
			>
				<Trans
					i18nKey="assets.unpair_confirm_instruction"
					values={{ confirmText: confirmUnpairText }}
					components={{ i: <i /> }}
					parent="span"
				/>
			</VelavuAlertModalTextConfirm>
		)).then(async (result) => {
			if (result) {
				setActionInProgress("unpair");

				for (let i = 0; i < selectedAssets.length; i++) {
					try {
						const updatedAsset =
							await VelavuAPI.assets.modifySpecificAsset(
								selectedAssets[i],
								{
									device_id:
										MODIFY_SPECIFIC_ASSET_UNPAIR_DEVICE_ID,
								},
							);

						emitter.emit("asset", {
							id: selectedAssets[i],
							data: updatedAsset,
						});
						setActionProgress(i + 1);
					} catch (error) {
						console.error(error);
					}
				}

				setSelectedAssets([]);
				setActionInProgress(undefined);
				setActionProgress(0);
			}
		});
	}, [emitter, pushModal, selectedAssets, t]);

	const selectAsset = useCallback(
		(selected: boolean, assetID: string) => {
			setSelectedAssets((selectedAssets) => {
				if (selected) {
					return [...selectedAssets, assetID];
				} else {
					return selectedAssets.filter((id) => id !== assetID);
				}
			});
		},
		[setSelectedAssets],
	);

	const selectAllAssets = useCallback(() => {
		setSelectedAssets((selectedAssets) => {
			if (selectedAssets.length > 0) {
				return [];
			} else {
				return displayAssets.map((asset) => asset.id);
			}
		});
	}, [displayAssets, setSelectedAssets]);

	//The category must be set by the categorization.
	//If the category changes, the categorization is invalid.
	useEffect(() => {
		setFilterCategorization((categorization) => {
			if (
				categorization !== null &&
				categorization.category !== filterCategory
			) {
				return null;
			} else {
				return categorization;
			}
		});
	}, [filterCategory, setFilterCategorization]);

	useEffect(() => {
		if (filterCategorization !== null) {
			setFilterCategory(filterCategorization.category);
		}
	}, [filterCategorization, setFilterCategory]);

	return (
		<>
			{actionInProgress && (
				<AssetActionInProgessModal
					title={
						actionInProgress === "delete"
							? t("assets.deleting_title")
							: t("assets.unpairing_title")
					}
					action={actionInProgress}
					progressCurrent={actionProgress}
					progressTotal={selectedAssets.length}
				/>
			)}
			<ListHeader
				icon={makeStyleable(IconVelavuAssets)}
				title={t("sidebar.nav.assets")}
				search={{
					searchPlaceholder: t("common.search"),
					searchText: searchText,
					setSearchText: setSearchText,
				}}
				add={
					canWriteAssets
						? {
								label: t("assets.add_asset"),
								route: "/assets/create",
							}
						: undefined
				}
			/>

			<ScreenContentContainer className={styles.container}>
				<AssetFilterMenu
					filterCategory={filterCategory}
					setFilterCategory={setFilterCategory}
					categorizations={assetCategorizations}
					filterCategorization={filterCategorization}
					setFilterCategorization={setFilterCategorization}
					filterStatus={filterStatus}
					setFilterStatus={setFilterStatus}
					filterUnpaired={filterUnpaired}
					setFilterUnpaired={setFilterUnpaired}
					selectedAssets={selectedAssets}
					onApply={applyFilters}
					onUnpair={unpairSelectedAssets}
					onDelete={deleteSelectedAssets}
					isDirty={isDirty}
					canUnpair={canWriteAssets}
					canDelete={canDeleteAssets}
				/>

				{assetsState.isLoading ? (
					<Loading />
				) : displayAssets.length === 0 ? (
					<NoAssets />
				) : (
					<div
						ref={scrollableBodyRef}
						className={styles.tableWrapper}
					>
						<WeightedTable
							className={styles.table}
							layout={[
								{ width: 48 },
								{ flex: 1 },
								{ flex: 1 },
								{ flex: 1 },
								{ flex: 1 },
								{ width: 60 },
							]}
						>
							<thead
								className={classArr(
									tableStyles.scrollableTableHeader,
								)}
							>
								<tr>
									<th>
										<Checkbox
											checked={selectedAssets.length > 0}
											onClick={selectAllAssets}
											master
										/>
									</th>
									<th>{t("assets.table_name")}</th>
									<th>{t("assets.table_category")}</th>
									<th>{t("assets.table_group")}</th>
									<th>{t("assets.table_tag")}</th>
									<th
										aria-label={t("assets.table_actions")}
									></th>
								</tr>
							</thead>

							<tbody>
								{displayAssets.map((asset) => (
									<AssetTableRow
										key={asset.id}
										asset={asset}
										selected={selectedAssets.includes(
											asset.id,
										)}
										onSelect={(selected) =>
											selectAsset(selected!, asset.id)
										}
										onDetails={() => inspectAsset(asset)}
										onPair={() =>
											inspectAsset(asset, "tag")
										}
										canPair={canWriteAssets}
									/>
								))}
							</tbody>
						</WeightedTable>
					</div>
				)}
			</ScreenContentContainer>

			<ListPagination
				totalPages={totalPages}
				currentPage={currentPage}
				setCurrentPage={setCurrentPage}
				setScrollToTop={setScrollToTop}
				hasMore={assetsState.hasMore}
				loadMore={loadMoreAssets}
				isLoading={assetsState.isLoading}
			/>
		</>
	);
}

function AssetTableRow(props: {
	asset: VelavuAsset;
	onDetails: VoidFunction;
	onPair: VoidFunction;
	onSelect: (selected?: boolean) => void;
	selected: boolean;
	canPair?: boolean;
}) {
	const [hoverState, enterHoverState, exitHoverState] = useToggleable();

	return (
		<tr
			className={styles.tableRow}
			onMouseEnter={enterHoverState}
			onMouseLeave={exitHoverState}
		>
			{/* Checkbox */}
			<td>
				<Checkbox checked={props.selected} onClick={props.onSelect} />
			</td>

			{/* Asset name */}
			<td onClick={props.onDetails}>
				<AssetName asset={props.asset} />
			</td>

			{/* Category */}
			<td onClick={props.onDetails}>
				<CategoryTag category={props.asset.category} />
			</td>

			{/* Group */}
			<td onClick={props.onDetails}>
				<GroupTag
					categorization={{
						category: props.asset.category,
						group: props.asset.group,
					}}
				/>
			</td>

			{/* Tag ID */}
			<td onClick={props.onDetails}>
				<AssetDevice
					asset={props.asset}
					onPair={props.onPair}
					canPair={props.canPair}
				/>
			</td>

			{/* Actions column */}
			<td onClick={props.onDetails} />
		</tr>
	);
}
